import type { ISchedule } from "@gettactic/api";
import { parseRRuleString } from "@/features/reserve/components/ReserveFieldRecurring/reserveRecurringUtils";

/**
 * A single one-off reservation, or a whole recurring series collapsed into one
 * entry. The API returns one row per materialised occurrence, so a daily desk
 * booking arrives as hundreds of rows — useless to read and misleading to
 * count.
 */
export type ResourceReservationGroup =
  | { kind: "single"; id: string; schedule: ISchedule }
  | {
      kind: "recurring";
      /** The master schedule's id — cancelling this cancels the series. */
      id: string;
      /** Occurrences that fell inside the queried window. */
      schedules: ISchedule[];
      first: ISchedule;
      last: ISchedule;
    };

/**
 * The id that ties occurrences of one series together.
 *
 * `recurring_schedule_id` is the master schedule and is what has to be
 * cancelled to remove the series, so it wins over the recurring task's own id.
 */
const getSeriesId = (schedule: ISchedule): string | null =>
  schedule.recurring_schedule_id || schedule.recurring_task?.id || null;

/**
 * Human-readable recurrence pattern, e.g. "every week on Monday, Tuesday".
 *
 * Returns null when the schedule is not recurring or its rule cannot be read —
 * callers fall back to naming the occurrences instead of showing a wrong
 * pattern.
 */
export const describeRecurrence = (schedule: ISchedule): string | null => {
  const rule = schedule.recurring_task?.rrule;
  if (!rule) return null;
  try {
    const text = parseRRuleString(rule).toText();
    return text ? text : null;
  } catch {
    return null;
  }
};

/**
 * Collapses a resource's reservations so each recurring series counts once.
 *
 * Order follows the earliest occurrence of each entry, so the list reads
 * chronologically whether or not a series is involved.
 */
export const groupResourceReservations = (
  schedules: readonly ISchedule[]
): ResourceReservationGroup[] => {
  const seenIds = new Set<string>();
  const singles: ResourceReservationGroup[] = [];
  const seriesById = new Map<string, ISchedule[]>();

  for (const schedule of schedules) {
    if (!schedule?.id || seenIds.has(schedule.id)) continue;
    seenIds.add(schedule.id);

    const seriesId = getSeriesId(schedule);
    if (!seriesId) {
      singles.push({ kind: "single", id: schedule.id, schedule });
      continue;
    }
    const existing = seriesById.get(seriesId);
    if (existing) {
      existing.push(schedule);
    } else {
      seriesById.set(seriesId, [schedule]);
    }
  }

  const groups: ResourceReservationGroup[] = [...singles];
  for (const [id, occurrences] of seriesById) {
    const sorted = [...occurrences].sort((a, b) =>
      (a.start ?? "").localeCompare(b.start ?? "")
    );
    groups.push({
      kind: "recurring",
      id,
      schedules: sorted,
      first: sorted[0],
      last: sorted[sorted.length - 1]
    });
  }

  return groups.sort((a, b) => {
    const aStart = a.kind === "single" ? a.schedule.start : a.first.start;
    const bStart = b.kind === "single" ? b.schedule.start : b.first.start;
    return (aStart ?? "").localeCompare(bStart ?? "");
  });
};

export interface ReservationCancelTarget {
  /** Always a real schedule id — never a recurring-task id. */
  scheduleId: string;
  resourceId: string;
  /** Cancel this occurrence and every later one in the series. */
  recurring: boolean;
}

/**
 * What to send to `schedules.delete` to clear these reservations.
 *
 * A series is cancelled once rather than occurrence by occurrence, which would
 * both hammer the API and miss the occurrences outside the queried window.
 * Which id to send depends on what the series exposes:
 *
 *  - `recurring_schedule_id` present — send the master id, which cancels the
 *    whole series.
 *  - otherwise — send the earliest occurrence's own id with `recurring: true`,
 *    cancelling from there forward. The group key can be a recurring-task id,
 *    and a task id is not a schedule id: sending it would fail the delete
 *    *after* the desk was already dedicated, leaving the series in place.
 */
export const getReservationCancelTargets = (
  groups: readonly ResourceReservationGroup[]
): ReservationCancelTarget[] =>
  groups.map((group) => {
    if (group.kind === "single") {
      return {
        scheduleId: group.schedule.id,
        resourceId: group.schedule.resource?.id ?? "",
        recurring: false
      };
    }
    const masterId = group.first.recurring_schedule_id;
    return {
      scheduleId: masterId || group.first.id,
      resourceId: group.first.resource?.id ?? "",
      recurring: !masterId
    };
  });
